import {
  Bell,
  BookOpen,
  CheckCircle2,
  Inbox,
  Megaphone,
  MessageSquare,
  Brain,
  Ticket,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  InboxListItemViewModel,
  NotificationCenterTab,
  NotificationListItemViewModel,
} from "@/hooks/useNotifications";

const iconToneMap = {
  blue: "bg-slate-100 text-slate-700",
  emerald: "bg-slate-100 text-slate-700",
  amber: "bg-slate-100 text-slate-700",
  indigo: "bg-slate-100 text-slate-700",
  purple: "bg-slate-100 text-slate-700",
} as const;

const renderIcon = (kind: NotificationListItemViewModel["iconKind"] | InboxListItemViewModel["iconKind"]) => {
  switch (kind) {
    case "resource":
      return <BookOpen className="h-4 w-4" />;
    case "follower":
      return <User className="h-4 w-4" />;
    case "submission":
      return <CheckCircle2 className="h-4 w-4" />;
    case "message":
      return <MessageSquare className="h-4 w-4" />;
    case "ticket":
      return <Ticket className="h-4 w-4" />;
    case "toofi":
      return <Brain className="h-4 w-4" />;
    case "inbox":
      return <Inbox className="h-4 w-4" />;
    case "announcement":
    default:
      return <Megaphone className="h-4 w-4" />;
  }
};

function NotificationCenterItem({
  item,
  onClick,
}: {
  item: NotificationListItemViewModel | InboxListItemViewModel;
  onClick: () => void;
}) {
  const isInboxItem = "priority" in item;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "m-2 flex w-[calc(100%-1rem)] items-start gap-4 rounded-[1.5rem] p-5 text-left transition-all duration-150 ease-out active:scale-95 border border-transparent",
        item.isUnread ? "bg-slate-50 hover:bg-slate-100/80 shadow-sm border-slate-200/50" : "bg-transparent hover:bg-slate-50/50",
      )}
    >
      <div className={cn("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full", iconToneMap[item.accentTone])}>
        {renderIcon(item.iconKind)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={cn("text-sm leading-tight", item.isUnread ? "font-semibold text-slate-950" : "font-medium text-slate-800")}>
            {item.title}
          </span>
          {"badgeLabel" in item && item.badgeLabel ? (
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em]",
                isInboxItem
                  ? item.priority === "urgent"
                    ? "bg-amber-600 text-white"
                    : "bg-slate-900 text-white"
                  : "bg-amber-100 text-amber-700",
              )}
            >
              {item.badgeLabel}
            </span>
          ) : null}
        </div>
        <p className="mt-1 line-clamp-2 text-[13px] leading-snug text-slate-600">{item.body}</p>
        {"explanation" in item && item.explanation ? (
          <p className="mt-1 line-clamp-2 text-[11px] leading-5 text-slate-500">{item.explanation}</p>
        ) : null}
        {"destinationLabel" in item && item.destinationLabel ? (
          <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{item.destinationLabel}</p>
        ) : null}
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{item.timestampLabel}</p>
      </div>
      {item.isUnread ? (
        <div className={cn("mt-2 h-2 w-2 shrink-0 rounded-full", isInboxItem ? "bg-amber-500" : "bg-primary")} />
      ) : null}
    </button>
  );
}

export function HeaderNotificationCenter({
  tab,
  onTabChange,
  notifications,
  inboxItems,
  unreadCount,
  inboxUnreadCount,
  activePanelTitle,
  activePanelSubtitle,
  canManagePush,
  isPushLoading,
  pushButtonLabel,
  onPushToggle,
  onMarkAllNotificationsRead,
  onMarkAllInboxRead,
  onNotificationSelect,
  onInboxSelect,
  showInboxTab,
  compact = false,
}: {
  tab: NotificationCenterTab;
  onTabChange: (tab: NotificationCenterTab) => void;
  notifications: NotificationListItemViewModel[];
  inboxItems: InboxListItemViewModel[];
  unreadCount: number;
  inboxUnreadCount: number;
  activePanelTitle: string;
  activePanelSubtitle: string;
  canManagePush: boolean;
  isPushLoading: boolean;
  pushButtonLabel: string;
  onPushToggle: () => void;
  onMarkAllNotificationsRead: () => void;
  onMarkAllInboxRead: () => void;
  onNotificationSelect: (id: string, link: string | null) => void;
  onInboxSelect: (id: string, link: string | null) => void;
  showInboxTab: boolean;
  compact?: boolean;
}) {
  const activeUnreadCount = tab === "notifications" ? unreadCount : inboxUnreadCount;

  return (
    <>
      <div className={cn("px-4 py-3", compact && "border-b border-border/40 pb-4")}>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-0.5">
            <span className={cn("font-semibold text-slate-950", compact ? "text-lg" : "text-[15px]")}>{activePanelTitle}</span>
            <p className={cn("text-slate-500", compact ? "text-xs" : "text-[11px]")}>{activePanelSubtitle}</p>
          </div>
          <span className="inline-flex h-7 items-center rounded-full border-transparent bg-slate-100/60 px-2.5 text-[11px] font-semibold text-slate-700">
            {activeUnreadCount} unread
          </span>
        </div>

        {showInboxTab ? (
          <div className="mt-3 grid grid-cols-2 gap-2 rounded-full bg-slate-100 p-1">
            <button
              type="button"
              className={cn(
                "rounded-full px-3 py-2 text-xs font-semibold transition-colors",
                tab === "notifications" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600",
              )}
              onClick={() => onTabChange("notifications")}
            >
              Notifications
            </button>
            <button
              type="button"
              className={cn(
                "rounded-full px-3 py-2 text-xs font-semibold transition-colors",
                tab === "inbox" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600",
              )}
              onClick={() => onTabChange("inbox")}
            >
              Inbox
            </button>
          </div>
        ) : null}

        {tab === "notifications" && (canManagePush || unreadCount > 0) ? (
          <div className={cn("mt-3 grid gap-2", canManagePush && unreadCount > 0 ? "grid-cols-2" : "grid-cols-1")}>
            {canManagePush ? (
              <button
                onClick={onPushToggle}
                disabled={isPushLoading}
                className="inline-flex h-11 items-center justify-center rounded-full bg-transparent border-transparent px-3 text-xs font-bold text-amber-700 transition-all duration-150 ease-out active:scale-95 hover:bg-amber-50/50 disabled:cursor-not-allowed disabled:opacity-60 sm:h-9 sm:font-semibold"
              >
                {isPushLoading ? "Updating..." : pushButtonLabel}
              </button>
            ) : null}
            {unreadCount > 0 ? (
              <button
                onClick={onMarkAllNotificationsRead}
                className="inline-flex h-11 items-center justify-center rounded-full bg-transparent border-transparent px-3 text-xs font-bold text-slate-600 transition-all duration-150 ease-out active:scale-95 hover:bg-slate-100 sm:h-9 sm:font-semibold"
              >
                Mark all read
              </button>
            ) : null}
          </div>
        ) : null}

        {tab === "inbox" && inboxUnreadCount > 0 ? (
          <div className="mt-3">
            <button
              onClick={onMarkAllInboxRead}
              className="inline-flex h-11 w-full items-center justify-center rounded-full bg-transparent border-transparent px-3 text-xs font-bold text-slate-600 transition-all duration-150 ease-out active:scale-95 hover:bg-slate-100 sm:h-9 sm:font-semibold"
            >
              Mark inbox read
            </button>
          </div>
        ) : null}
      </div>

      <div className={cn(compact ? "flex-1 overflow-y-auto pt-4" : "max-h-[350px] overflow-y-auto overflow-x-hidden pb-2", "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]")}>
        {tab === "notifications" ? (
          notifications.length === 0 ? (
            <div className={cn("flex flex-col items-center justify-center text-center", compact ? "py-12 gap-2" : "px-4 py-12 gap-2")}>
              <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <Bell className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-slate-950">You're all caught up!</p>
              <p className="text-xs text-slate-500">We'll notify you when something happens.</p>
            </div>
          ) : (
            <div className={cn(compact ? "space-y-4 pb-8" : "space-y-2")}>
              {notifications.map((item) => (
                <NotificationCenterItem key={item.id} item={item} onClick={() => onNotificationSelect(item.id, item.link)} />
              ))}
            </div>
          )
        ) : inboxItems.length === 0 ? (
          <div className={cn("flex flex-col items-center justify-center text-center", compact ? "py-12 gap-2" : "px-4 py-12 gap-2")}>
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 text-amber-500">
              <Inbox className="h-5 w-5" />
            </div>
            <p className="text-sm font-medium text-slate-950">No inbox messages yet</p>
            <p className="text-xs text-slate-500">Admin and support messages will appear here.</p>
          </div>
        ) : (
          <div className={cn(compact ? "space-y-4 pb-8" : "space-y-2")}>
            {inboxItems.map((item) => (
              <NotificationCenterItem key={item.id} item={item} onClick={() => onInboxSelect(item.id, item.link)} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
